import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";
import { API } from "../lib/api";

// Landing-page "Try Mimi Live" — full-width in-page chat panel. Visitors are
// restaurant owners evaluating BuzzerAI, so Mimi pitches the product itself
// (backend flips into "sales concierge" mode when demo_mode=true).
// Backend rate-limits demo sessions to 8 msgs / 24h and caches the six starter
// chips so a viral spike doesn't torch the OpenAI bill.
const DEMO_SID_KEY = "buzzerai_demo_sid";
const demoSid = () => {
  let s = sessionStorage.getItem(DEMO_SID_KEY);
  if (!s) {
    s = `landing-demo-${crypto.randomUUID()}`;
    sessionStorage.setItem(DEMO_SID_KEY, s);
  }
  return s;
};

const STARTERS = [
  { icon: "💵", label: "How much does BuzzerAI cost?", tag: "pricing" },
  { icon: "⚔️", label: "How are you different from Waitwhile?", tag: "vs-waitwhile" },
  { icon: "📞", label: "What does Twilio actually cost me?", tag: "twilio-cost" },
  { icon: "🎁", label: "Is there a free trial?", tag: "trial" },
  { icon: "⏱️", label: "How long does setup take?", tag: "setup" },
  { icon: "🏪", label: "Does this work for a small independent restaurant?", tag: "indie-fit" },
];

export default function TryMimiLive() {
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi, I'm Mimi — the AI concierge for BuzzerAI. Ask me anything about how we help independent restaurants: how the AI phone receptionist works, what it costs, why we're different from Waitwhile or OpenTable, or how to start a free trial. What can I help you with?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [starterHidden, setStarterHidden] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  const send = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || busy) return;
    setStarterHidden(true);
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: clean }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: demoSid(),
          message: clean,
          business_slug: "marios",
          demo_mode: true,
        }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMsgs((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Try refreshing the page and asking again." };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <section id="try-mimi" className="px-6 pb-24" data-testid="try-mimi-live">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <div className="overline text-[#D96C5B] mb-2 inline-flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D96C5B] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D96C5B]" />
            </span>
            LIVE · This is the real Mimi
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-3">
            Waitwhile digitizes a clipboard. <span className="text-[#5C6B61]">We answer the phone.</span>
          </h2>
          <p className="text-sm text-[#5C6B61] max-w-2xl mx-auto leading-relaxed">
            This is Mimi in <b className="text-[#121914]">sales mode</b> — ask her anything about BuzzerAI. Pricing, how we stack up against Waitwhile / OpenTable / Yelp, what Twilio really costs, how the free trial works, how fast you can be live. She'll give it to you straight, no sales BS.
          </p>
        </div>

        <div className="paper-card p-0 overflow-hidden shadow-xl">
          <div className="bg-[#1E3A2F] text-white px-5 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Mimi · Sales Concierge</div>
              <div className="font-heading font-extrabold text-sm">BuzzerAI</div>
            </div>
            <div className="text-[10px] text-white/70">powered by BuzzerAI</div>
          </div>

          <div
            ref={scrollRef}
            className="bg-[#F7F6F2] px-5 py-4 space-y-3 overflow-y-auto"
            style={{ height: 340 }}
            data-testid="try-mimi-transcript"
          >
            {msgs.map((m, i) => (
              <div key={`try-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1E3A2F] text-white"
                      : "bg-white border border-[#E1DFD5] text-[#121914]"
                  }`}
                >
                  {m.content || <span className="text-[#5C6B61] italic">Mimi is thinking…</span>}
                </div>
              </div>
            ))}
          </div>

          {!starterHidden && (
            <div className="bg-white border-t border-[#E1DFD5] px-5 py-3" data-testid="try-mimi-starters">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6B61] mb-2 font-semibold">Try one of these ↓</div>
              <div className="flex flex-wrap gap-2">
                {STARTERS.map((s) => (
                  <button
                    key={s.tag}
                    onClick={() => send(s.label)}
                    disabled={busy}
                    className="text-xs bg-[#F7F6F2] hover:bg-[#E6E2D6] border border-[#E1DFD5] rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                    data-testid={`try-mimi-starter-${s.tag}`}
                  >
                    <span className="mr-1">{s.icon}</span>{s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border-t border-[#E1DFD5] p-3 flex items-center gap-2">
            <input
              className="input-field !py-2 flex-1"
              placeholder="Or type your own — 'Can I keep my current phone number?', 'What if I cancel?', anything…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              disabled={busy}
              data-testid="try-mimi-input"
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#1E3A2F] flex items-center justify-center disabled:opacity-50"
              data-testid="try-mimi-send"
              aria-label="Send message to Mimi"
            >
              <PaperPlaneTilt size={16} color="#fff" weight="fill" />
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
          <p className="text-[#5C6B61] text-center max-w-md">
            <b className="text-[#121914]">Same Mimi. Your restaurant. $29–$99/month, no contract, cancel in one tap.</b> The Yelps and OpenTables of the world charge triple for less.
          </p>
          <Link
            to="/signup"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="try-mimi-cta-signup"
          >
            Start free · 14-day trial <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
